import { useOrdersStore } from "./use-orders-store";
import { OrderPriority } from "./order-priority";
import { OrderElapsedTime } from "./order-elapsed-time";
import { OrderTotal } from "./order-total";
import { cn } from "@/lib/utils";

import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/atoms/table";
import { Card, CardContent } from "@/components/atoms/card";
import OrderRowDropdown from "@/components/orders/OrderRowDropdown";
import StatusBadge from "@/components/orders/StatusBadge";
import { Skeleton } from "@/components/atoms/skeleton";
import { useNavigate } from "react-router-dom";

export default function OrderTable() {
  const isLoading = useOrdersStore((state) => state.isLoading);
  const isSearching = useOrdersStore((state) => state.isSearching);
  const paginatedOrdersCurrentDay = useOrdersStore(
    (state) => state.paginatedOrdersCurrentDay,
  );
  const fetchPaginatedOrdersCurrentDay = useOrdersStore(
    (state) => state.fetchPaginatedOrdersCurrentDay,
  );
  const currentPage = useOrdersStore((state) => state.currentPage);
  const fetchOptions = useOrdersStore((state) => state.fetchOptions);
  const setOrderDetail = useOrdersStore((state) => state.setOrderDetail);
  const trackingIdFilter = useOrdersStore((state) => state.trackingIdFilter);
  const hasInitiallyFetched = useOrdersStore(
    (state) => state.hasInitiallyFetched,
  );
  const navigate = useNavigate();
  const handleOrderClick = (id: any) => {
    setOrderDetail(id);
    navigate(`/orders/${id}`);
  };

  // Add proper callback functions for order updates
  const handleOrderStatusUpdate = async () => {
    // Refresh the current page of orders to reflect the status change
    await fetchPaginatedOrdersCurrentDay(currentPage, fetchOptions);
  };

  const handleOrderDriverUpdate = async () => {
    // Refresh the current page of orders to reflect the driver assignment
    await fetchPaginatedOrdersCurrentDay(currentPage, fetchOptions);
  };

  // Show loading skeleton only for initial load, not for searches
  if (isLoading && !hasInitiallyFetched) {
    return (
      <div className="w-full">
        {/* Desktop skeleton */}
        <div className="hidden md:block">
          <Table>
            <TableCaption className="sr-only">
              Revisa los estados de las órdenes del día
            </TableCaption>
            <TableHeader>
              <TableRow className="bg-gray-50 rounded-full">
                <TableHead className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-600 first:rounded-l-full last:rounded-r-full">
                  Tracking #
                </TableHead>
                <TableHead className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-600">
                  Prioridad
                </TableHead>
                <TableHead className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-600">
                  Estado
                </TableHead>
                <TableHead className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-600">
                  Sucursal
                </TableHead>
                <TableHead className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-600">
                  Creación
                </TableHead>
                <TableHead className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-600">
                  Total
                </TableHead>
                <TableHead className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-600">
                  Conductor
                </TableHead>
                <TableHead className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-600 text-center last:rounded-r-full">
                  Acciones
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>{renderSkeletonRows()}</TableBody>
          </Table>
        </div>

        {/* Mobile skeleton */}
        <div className="md:hidden space-y-4">
          {Array.from({ length: 3 }, (_, index) => (
            <Card key={`mobile-skeleton-${index}`} className="p-4">
              <div className="space-y-3">
                <div className="flex justify-between items-start">
                  <Skeleton className="h-5 w-24 bg-gray-200" />
                  <Skeleton className="h-6 w-16 rounded-full bg-gray-200" />
                </div>
                <div className="flex justify-between items-center">
                  <Skeleton className="h-6 w-20 rounded-full bg-gray-200" />
                  <Skeleton className="h-4 w-16 bg-gray-200" />
                </div>
                <div className="flex justify-between items-center">
                  <Skeleton className="h-4 w-32 bg-gray-200" />
                  <Skeleton className="h-4 w-20 bg-gray-200" />
                </div>
                <div className="flex justify-between items-center">
                  <Skeleton className="h-4 w-28 bg-gray-200" />
                  <Skeleton className="h-8 w-8 rounded bg-gray-200" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Show no results message for searches or when no orders exist
  if (paginatedOrdersCurrentDay.length === 0) {
    const isSearchResult = trackingIdFilter.trim() !== "";

    return (
      <div className="w-full">
        <div className="text-center py-8">
          {isSearchResult ? (
            <div className="space-y-2">
              <div className="text-gray-500 font-medium">
                No se encontraron órdenes con el tracking ID "{trackingIdFilter}
                "
              </div>
              <div className="text-sm text-gray-400">
                Intenta con un ID diferente o verifica el número de tracking
              </div>
            </div>
          ) : (
            <div className="text-gray-500">No hay órdenes para mostrar</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Desktop Table View */}
      <div className="hidden xl:block overflow-x-auto rounded-lg bg-white">
        <Table>
          <TableCaption className="sr-only">
            Revisa los estados de las órdenes del día
          </TableCaption>
          <TableHeader>
            <TableRow className="bg-gray-50 rounded-full">
              <TableHead className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-600 first:rounded-l-full last:rounded-r-full">
                Tracking #
              </TableHead>
              <TableHead className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-600">
                Prioridad
              </TableHead>
              <TableHead className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-600">
                Estado
              </TableHead>
              <TableHead className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-600">
                Sucursal
              </TableHead>
              <TableHead className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-600">
                Creación
              </TableHead>
              <TableHead className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-600">
                Total
              </TableHead>
              <TableHead className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-600">
                Conductor
              </TableHead>
              <TableHead className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-600 text-center last:rounded-r-full">
                Acciones
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isSearching && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                    <span className="text-sm text-gray-600">
                      Buscando órdenes...
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {paginatedOrdersCurrentDay.map((order) => (
              <TableRow
                key={order.id}
                onClick={() => handleOrderClick(order.id)}
                className="group cursor-pointer transition-colors duration-100 ease-out hover:bg-gray-50"
              >
                <TableCell className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">
                  {order.tracking_id}
                </TableCell>
                <TableCell className="whitespace-nowrap px-4 py-3">
                  <OrderPriority priority={order.priority} />
                </TableCell>
                <TableCell className="whitespace-nowrap px-4 py-3">
                  <StatusBadge status={order.status} type="order" />
                </TableCell>
                <TableCell className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">
                  {order.branch?.name}
                </TableCell>
                <TableCell className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">
                  <OrderElapsedTime createdAt={order.created_at} />
                </TableCell>
                <TableCell className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">
                  <OrderTotal order={order} />
                </TableCell>
                <TableCell className="whitespace-nowrap px-4 py-3 text-sm">
                  <span className={cn(!order.driver?.name && "text-gray-400")}>
                    {order.driver?.name || "Sin asignar"}
                  </span>
                </TableCell>
                <TableCell className="whitespace-nowrap px-4 py-3 text-center">
                  {order.status !== "completed" &&
                  order.status !== "cancelled" ? (
                    <OrderRowDropdown
                      order={order}
                      onOrderStatusUpdate={handleOrderStatusUpdate}
                      onOrderDriverUpdate={handleOrderDriverUpdate}
                    />
                  ) : (
                    <span className="text-gray-400 h-8 w-12 flex items-center justify-center">
                      -
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile Card View */}
      <div className="xl:hidden space-y-3 px-1">
        {isSearching && (
          <Card className="p-4">
            <div className="flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
              <span className="text-sm text-gray-600">Buscando órdenes...</span>
            </div>
          </Card>
        )}
        {paginatedOrdersCurrentDay.map((order) => (
          <Card
            key={order.id}
            className="cursor-pointer transition-all duration-200 hover:shadow-md border border-gray-200 rounded-xl"
            onClick={() => handleOrderClick(order.id)}
          >
            <CardContent className="p-3 sm:p-4">
              <div className="space-y-2">
                {/* Header Row: Tracking ID + Priority */}
                <div className="flex flex-col xs:flex-row xs:justify-between xs:items-start gap-1">
                  <div>
                    <p className="text-[11px] xs:text-xs font-medium text-gray-500 leading-tight">
                      Tracking #
                    </p>
                    <p className="text-sm font-semibold text-gray-900 break-all">
                      {order.tracking_id}
                    </p>
                  </div>
                  <div className="mt-1 xs:mt-0 flex-shrink-0">
                    <OrderPriority priority={order.priority} />
                  </div>
                </div>

                {/* Status + Creation Time */}
                <div className="flex flex-col xs:flex-row xs:justify-between xs:items-center gap-1">
                  <div>
                    <p className="text-[11px] xs:text-xs font-medium text-gray-500 leading-tight">
                      Estado
                    </p>
                    <div className="mt-0.5">
                      <StatusBadge status={order.status} type="order" />
                    </div>
                  </div>
                  <div className="xs:text-right mt-1 xs:mt-0">
                    <p className="text-[11px] xs:text-xs font-medium text-gray-500 leading-tight">
                      Creación
                    </p>
                    <p className="text-sm text-gray-900">
                      <OrderElapsedTime createdAt={order.created_at} />
                    </p>
                  </div>
                </div>

                {/* Branch + Total */}
                <div className="flex flex-col xs:flex-row xs:justify-between xs:items-center gap-1">
                  <div>
                    <p className="text-[11px] xs:text-xs font-medium text-gray-500 leading-tight">
                      Sucursal
                    </p>
                    <p className="text-sm text-gray-900 break-all">
                      {order.branch?.name}
                    </p>
                  </div>
                  <div className="xs:text-right mt-1 xs:mt-0">
                    <p className="text-[11px] xs:text-xs font-medium text-gray-500 leading-tight">
                      Total
                    </p>
                    <p className="text-sm font-semibold text-gray-900">
                      <OrderTotal order={order} />
                    </p>
                  </div>
                </div>

                {/* Driver + Actions */}
                <div className="flex flex-col xs:flex-row xs:justify-between xs:items-center pt-2 border-t border-gray-100 gap-1">
                  <div>
                    <p className="text-[11px] xs:text-xs font-medium text-gray-500 leading-tight">
                      Conductor
                    </p>
                    <p
                      className={cn(
                        "text-sm",
                        !order.driver?.name && "text-gray-400",
                      )}
                    >
                      {order.driver?.name || "Sin asignar"}
                    </p>
                  </div>
                  <div
                    className="mt-1 xs:mt-0 flex-shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {order.status !== "completed" &&
                    order.status !== "cancelled" ? (
                      <OrderRowDropdown
                        order={order}
                        onOrderStatusUpdate={handleOrderStatusUpdate}
                        onOrderDriverUpdate={handleOrderDriverUpdate}
                      />
                    ) : (
                      "-"
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

const renderSkeletonRows = () => {
  return Array.from({ length: 5 }, (_, index) => (
    <TableRow key={`skeleton-${index}`}>
      <TableCell className="whitespace-nowrap px-4 py-3">
        <Skeleton className="h-4 w-24 bg-gray-200" />
      </TableCell>
      <TableCell className="whitespace-nowrap px-4 py-3">
        <Skeleton className="h-6 w-16 rounded-full bg-gray-200" />
      </TableCell>
      <TableCell className="whitespace-nowrap px-4 py-3">
        <Skeleton className="h-6 w-20 rounded-full bg-gray-200" />
      </TableCell>
      <TableCell className="whitespace-nowrap px-4 py-3">
        <Skeleton className="h-4 w-32 bg-gray-200" />
      </TableCell>
      <TableCell className="whitespace-nowrap px-4 py-3">
        <Skeleton className="h-4 w-16 bg-gray-200" />
      </TableCell>
      <TableCell className="whitespace-nowrap px-4 py-3">
        <Skeleton className="h-4 w-20 bg-gray-200" />
      </TableCell>
      <TableCell className="whitespace-nowrap px-4 py-3">
        <Skeleton className="h-4 w-28 bg-gray-200" />
      </TableCell>
      <TableCell className="whitespace-nowrap px-4 py-3 text-center">
        <Skeleton className="h-8 w-8 rounded bg-gray-200 mx-auto" />
      </TableCell>
    </TableRow>
  ));
};
